import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function MerchantCenterInventory() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [brandSummaryFull, setBrandSummaryFull] = useState([]);
  const [categorySummaryFull, setCategorySummaryFull] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [history, setHistory] = useState([]); // daily rollup time series
  useEffect(() => { setActiveImageIdx(0); }, [selectedProduct]);
  const PAGE_SIZE = 10;
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [pageIndex, setPageIndex] = useState(1); // 1-based (client-side)
  const [hasMoreServerPages, setHasMoreServerPages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const DEMO_BRAND_IDS_MC = new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']);

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  }, []);

  // Rollup for active products. Prefer DB history last point; fallback to live status structure if present.
  const activeRollup = useMemo(() => {
    if (Array.isArray(history) && history.length > 0) {
      const last = history[history.length - 1];
      return {
        active: Number(last.active || 0),
        pending: Number(last.pending || 0),
        disapproved: Number(last.disapproved || 0),
        expiring: Number(last.expiring || 0),
        destination: last.destination || '',
        country: last.country || ''
      };
    }
    const productsArr = status?.status?.products || [];
    const preferred = ['SurfacesAcrossGoogle', 'Shopping', 'DisplayAds'];
    const picked = preferred.map(d => productsArr.find(p => p?.destination === d)).find(Boolean) || productsArr[0];
    if (!picked) return null;
    const st = picked.statistics || {};
    return {
      active: Number(st.active || 0),
      pending: Number(st.pending || 0),
      disapproved: Number(st.disapproved || 0),
      expiring: Number(st.expiring || 0),
      destination: picked.destination,
      country: picked.country
    };
  }, [status, history]);

  // append today's point to local history
  useEffect(() => {
    if (!merchantId) return;
    const key = `mc_status_history_${merchantId}`;
    let local = [];
    try {
      const raw = localStorage.getItem(key);
      if (raw) local = JSON.parse(raw) || [];
    } catch (_) {}
    setHistory(Array.isArray(local) ? local : []);
  }, [merchantId]);

  useEffect(() => {
    if (!merchantId || !activeRollup) return;
    const key = `mc_status_history_${merchantId}`;
    const today = new Date().toISOString().slice(0, 10);
    const point = { date: today, active: activeRollup.active, pending: activeRollup.pending, disapproved: activeRollup.disapproved, expiring: activeRollup.expiring };
    setHistory(prev => {
      const others = (prev || []).filter(p => p.date !== today);
      const next = [...others, point].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      try { localStorage.setItem(key, JSON.stringify(next)); } catch(_) {}
      return next;
    });
  }, [merchantId, activeRollup]);

  // No line chart on inventory page

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        // Load merchantId from connections if not stored
        let mid = '';
        if (brandId) {
          // DEMO: Inflate inventory and categories for specific brand, short-circuit network
          if (DEMO_BRAND_IDS_MC.has(brandId)) {
            // 6-month exaggerated status history
            const now = new Date();
            const months = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(now);
              d.setDate(1);
              d.setMonth(d.getMonth() - (5 - i));
              return d.toISOString().slice(0, 10);
            });
            const demoHistory = months.map((dt, idx) => ({
              date: dt,
              active: 15000 + idx * 1200,
              pending: 500 + (5 - idx) * 40,
              disapproved: 180 + Math.max(0, 4 - idx) * 15,
              expiring: 320 + idx * 18,
              destination: 'Shopping',
              country: 'TR',
            }));
            // Rich category space
            const categoryPool = [
              'Kadın > Spor Ayakkabı > Koşu',
              'Kadın > Spor Ayakkabı > Yürüyüş',
              'Kadın > Giyim > Eşofman',
              'Erkek > Spor Ayakkabı > Koşu',
              'Erkek > Spor Ayakkabı > Yürüyüş',
              'Erkek > Outdoor > Trekking',
              'Çocuk > Spor Ayakkabı',
              'Çocuk > Okul > Spor',
              'Anne & Bebek > Bebek Arabası',
              'Anne & Bebek > Taşıma > Çanta',
              'Spor > Outdoor > Hiking',
              'Spor > Fitness > Aksesuar',
              'Outdoor > Hiking > Bot',
              'Outdoor > Kamp > Ekipman',
              'Koşu > Ayakkabı',
              'Yürüyüş > Ayakkabı',
              'Ev & Yaşam > Spor Ekipmanları',
              'Elektronik > Giyilebilir > Spor Saat',
            ];
            // Brand mix (removed Skechers, Kinetix, Asics, Lumberjack as requested)
            const brands = ['Nike','Adidas','Puma','New Balance','Hummel','Reebok'];
            const images = [
              'https://skcfiles.mncdn.com/livephotos/8/149807-OFWT/03.jpg',
              'https://cdn2.a101.com.tr/dbmk89vnr/CALL/Image/getcl/livephotos/8/26032267MAVI/1.jpg',
              'https://static.ticimax.cloud/3402/uploads/urunresimleri/buyuk/go-run-consistent-2-f55b-e.jpg',
              'https://skcfiles.mncdn.com/livephotos/8/232700tk-wht/1.jpg',
              'https://images.skechers.com/image;width=400%2Cformat=auto/150510_GYPK_HERO_LG',
              'https://media.kohlsimg.com/is/image/kohls/2528936_Gray_White_Leather?wid=1000&hei=1000&op_sharpen=1',
            ];
            // Generate more products to boost brand/category totals
            const totalItems = 480;
            const demoProducts = Array.from({ length: totalItems }, (_, i) => {
              const brand = brands[i % brands.length];
              const category = categoryPool[i % categoryPool.length];
              // Vary availability by category index to create diverse counts
              const catIndex = categoryPool.indexOf(category);
              const inStock = (i + catIndex) % 7 !== 6; // ~85% in stock on average, category-shifted
              const base = 899 + (i % 40) * 120; // 899..  5899 TRY range
              const price = base + (i % 5) * 70;
              const isSale = i % 4 === 0;
              const sale = isSale ? Math.max(499, price - (150 + (i % 6) * 50)) : null;
              const leaf = (category.split('>').slice(-1)[0] || '').trim();
              const prefixes = ['Klasik', 'Basic', 'Premium', 'Comfort', 'Active', 'Urban', 'Essential', 'Core', 'Fit', 'Pro'];
              const sizes = ['XS', 'S', 'M', 'L', 'XL'];
              const title = `${prefixes[i % prefixes.length]} ${leaf} ${sizes[i % sizes.length]}`;
              return {
                offerId: `DEMO-OFFER-${20000 + i}`,
                productId: `DEMO-PROD-${20000 + i}`,
                title,
                brand,
                availability: inStock ? 'in stock' : 'out of stock',
                price: { value: price, currency: 'TRY' },
                salePrice: sale ? { value: sale, currency: 'TRY' } : null,
                imageLink: images[i % images.length],
                additionalImages: [],
                category,
                link: 'https://example.com/demo-product',
                contentLanguage: 'tr',
                targetCountry: 'TR',
                condition: 'new',
                gtin: `8699${20000 + i}`,
                customLabel0: 'Demo',
                customLabel1: (category.split('>')[0] || '').trim(),
                customLabel2: (category.split('>')[1] || '').trim(),
                customLabel3: (category.split('>')[2] || '').trim(),
                customLabel4: brand,
              };
            });
            setMerchantId('DEMO-MC-TR');
            setHistory(demoHistory);
            setProducts(demoProducts);
            setHasMoreServerPages(false);
            setStatus({
              status: {
                products: [
                  { destination: 'Shopping', statistics: { active: 21000, pending: 420, disapproved: 160, expiring: 560 } },
                  { destination: 'SurfacesAcrossGoogle', statistics: { active: 22500, pending: 380, disapproved: 150, expiring: 520 } },
                ],
              },
            });
            setLoading(false);
            return;
          }
          const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            let found = null;
            if (Array.isArray(data)) {
              found = data.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
            } else if (data && typeof data === 'object') {
              const mc = data['MERCHANT_CENTER'] || data['merchant_center'] || null;
              if (mc) found = mc;
            }
            mid = found?.customerId || found?.merchantId || '';
          }
        }
        if (mid) setMerchantId(String(mid));

        // Date window for DB reads (last 30 days)
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 30);
        const toISO = (d) => d.toISOString().split('T')[0];
        const startDate = toISO(start);
        const endDate = toISO(end);

        // Fetch account status from DB and build daily rollup
        if (mid && brandId) {
          const st = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/accountstatus/db?merchantId=${encodeURIComponent(mid)}&startDate=${startDate}&endDate=${endDate}`, {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          const stText = await st.text(); let stJson; try { stJson = JSON.parse(stText); } catch { stJson = { raw: stText }; }
          if (!st.ok) throw new Error(stJson?.error || st.statusText || 'Failed to load account status');
          const rows = Array.isArray(stJson?.data) ? stJson.data : [];
          // Pick per-date preferred destination: SurfacesAcrossGoogle > Shopping > DisplayAds (no summing)
          const priority = (dest) => dest === 'SurfacesAcrossGoogle' ? 3 : dest === 'Shopping' ? 2 : dest === 'DisplayAds' ? 1 : 0;
          const byDate = {};
          for (const r of rows) {
            const d = r.fetch_date || r.date;
            if (!d) continue;
            const prev = byDate[d];
            if (!prev || priority(r.destination) > priority(prev.destination)) {
              byDate[d] = {
                date: d,
                active: Number(r.active || 0),
                pending: Number(r.pending || 0),
                disapproved: Number(r.disapproved || 0),
                expiring: Number(r.expiring || 0),
                destination: r.destination || '',
                country: r.country || ''
              };
            }
          }
          const series = Object.values(byDate).sort((a, b) =>
            a.date < b.date ? -1 : a.date > b.date ? 1 : 0
          );
          setHistory(series);
        }

        // Fetch full inventory summary (brand & category aggregates) without loading all products
        if (mid && brandId) {
          const summaryRes = await fetch(
            `https://api.glowytics.com/api/google-ads/merchant-center/inventory/summary/db?merchantId=${encodeURIComponent(
              mid
            )}`,
            {
              headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
              credentials: 'include'
            }
          );
          const summaryText = await summaryRes.text();
          let summaryJson;
          try {
            summaryJson = JSON.parse(summaryText);
          } catch {
            summaryJson = { raw: summaryText };
          }
          if (!summaryRes.ok) {
            throw new Error(
              summaryJson?.error || summaryRes.statusText || 'Failed to load inventory summary'
            );
          }
          setBrandSummaryFull(Array.isArray(summaryJson?.brandSummary) ? summaryJson.brandSummary : []);
          setCategorySummaryFull(
            Array.isArray(summaryJson?.categorySummary) ? summaryJson.categorySummary : []
          );
        }

        // Fetch initial page of products (preview) from DB for aggregation + table.
        // We now load products incrementally in PAGE_SIZE chunks instead of pulling everything at once.
        if (mid && brandId) {
          const limit = PAGE_SIZE;
          const offset = 0;
          const pr = await fetch(
            `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(
              mid
            )}&limit=${limit}&offset=${offset}`,
            {
              headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
              credentials: 'include'
            }
          );
          const prText = await pr.text();
          let prJson;
          try {
            prJson = JSON.parse(prText);
          } catch {
            prJson = { raw: prText };
          }
          if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
          const rows = Array.isArray(prJson?.data) ? prJson.data : [];
          const list = rows.map((r) => ({
            offerId: r.offer_id,
            productId: r.product_id,
            title: r.title,
            brand: r.brand,
            availability: r.availability,
            price: { value: r.price_value, currency: r.price_currency },
            salePrice: r.sale_price_value
              ? { value: r.sale_price_value, currency: r.sale_price_currency }
              : null,
            imageLink: r.image_link,
            additionalImages: Array.isArray(r.additional_image_links)
              ? r.additional_image_links
              : [],
            category: r.google_product_category,
            link: r.link,
            contentLanguage: r.content_language,
            targetCountry: r.target_country,
            condition: r.condition,
            gtin: r.gtin,
            customLabel0: r.custom_label0,
            customLabel1: r.custom_label1,
            customLabel2: r.custom_label2,
            customLabel3: r.custom_label3,
            customLabel4: r.custom_label4
          }));
          setProducts(list);
          setPageIndex(1);
          setHasMoreServerPages(rows.length === limit);
        }
      } catch (e) {
        setErrorMsg(e?.message || 'Load error');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [brandId]);

  const Card = ({ label, value, sub, icon }) => (
    <div className="bg-white rounded-2xl border shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">{label}</div>
        {icon ? <div className="text-gray-400">{icon}</div> : null}
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );

  // Aggregations
  const normalize = (s) => String(s || '').trim().toLowerCase();
  const isInStock = (a) => normalize(a) === 'in stock';
  const isOutOfStock = (a) => normalize(a) === 'out of stock';

  const brandAgg = useMemo(() => {
    // Prefer full summary from backend when available
    if (brandSummaryFull && brandSummaryFull.length) {
      return brandSummaryFull;
    }
    const map = {};
    for (const p of products) {
      const key = p.brand || '(none)';
      if (!map[key]) map[key] = { key, total: 0, inStock: 0, outOfStock: 0 };
      map[key].total += 1;
      if (isInStock(p.availability)) map[key].inStock += 1;
      if (isOutOfStock(p.availability)) map[key].outOfStock += 1;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [products, brandSummaryFull]);

  // Category tree aggregation (split by '>' and roll up to parents)
  // If useWeights=true, items can carry _weightTotal/_weightInStock/_weightOutOfStock
  const buildCategoryTree = (items, useWeights = false) => {
    const makeNode = (key, name) => ({
      key,
      name,
      total: 0,
      inStock: 0,
      outOfStock: 0,
      children: new Map()
    });
    const root = makeNode('', '(root)');
    for (const p of items) {
      const catRaw = (p.category && String(p.category)) || '(none)';
      const segments = catRaw.split('>').map(s => String(s || '').trim()).filter(s => s);
      const path = segments.length ? segments : ['(none)'];
      let accKey = '';
      let node = root;
      // increment root
      const wTotal = useWeights ? Number(p._weightTotal || 0) : 1;
      const wInStock = useWeights ? Number(p._weightInStock || 0) : (isInStock(p.availability) ? 1 : 0);
      const wOutOfStock = useWeights ? Number(p._weightOutOfStock || 0) : (isOutOfStock(p.availability) ? 1 : 0);
      node.total += wTotal;
      node.inStock += wInStock;
      node.outOfStock += wOutOfStock;
      for (const seg of path) {
        accKey = accKey ? `${accKey} > ${seg}` : seg;
        if (!node.children.has(seg)) {
          node.children.set(seg, makeNode(accKey, seg));
        }
        const child = node.children.get(seg);
        child.total += wTotal;
        child.inStock += wInStock;
        child.outOfStock += wOutOfStock;
        node = child;
      }
    }
    return root;
  };
  const categoryTree = useMemo(() => {
    // If we have full category summary, build the tree using aggregated rows
    if (categorySummaryFull && categorySummaryFull.length) {
      // Expand each summary row into weighted counts at the tree level
      const root = buildCategoryTree(
        categorySummaryFull.map((row) => ({
          category: row.category || '(none)',
          availability: row.inStock > 0 ? 'in stock' : 'out of stock',
          _weightTotal: row.total,
          _weightInStock: row.inStock,
          _weightOutOfStock: row.outOfStock
        })),
        true
      );
      return root;
    }
    return buildCategoryTree(products, false);
  }, [products, categorySummaryFull]);
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  const toggleCat = (key) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Historical monthly columns removed per request

  // Debounce search before triggering server-side search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((search || '').trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side search: reload products from DB endpoint with q=... so search runs across all products
  useEffect(() => {
    const run = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId || !merchantId) return;
        if (DEMO_BRAND_IDS_MC.has(brandId)) return;

        setIsLoadingMore(true);
        setErrorMsg('');
        setPageIndex(1);
        setHasMoreServerPages(true);

        const searchLimit = debouncedSearch ? 5000 : PAGE_SIZE;
        const pr = await fetch(
          `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(merchantId)}&limit=${searchLimit}&offset=0${debouncedSearch ? `&q=${encodeURIComponent(debouncedSearch)}` : ''}`,
          {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include'
          }
        );
        const prText = await pr.text();
        let prJson;
        try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
        if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
        const rows = Array.isArray(prJson?.data) ? prJson.data : [];
        const list = rows.map((r) => ({
          offerId: r.offer_id,
          productId: r.product_id,
          title: r.title,
          brand: r.brand,
          availability: r.availability,
          price: { value: r.price_value, currency: r.price_currency },
          salePrice: r.sale_price_value ? { value: r.sale_price_value, currency: r.sale_price_currency } : null,
          imageLink: r.image_link,
          additionalImages: Array.isArray(r.additional_image_links) ? r.additional_image_links : [],
          category: r.google_product_category,
          link: r.link,
          contentLanguage: r.content_language,
          targetCountry: r.target_country,
          condition: r.condition,
          gtin: r.gtin,
          customLabel0: r.custom_label0,
          customLabel1: r.custom_label1,
          customLabel2: r.custom_label2,
          customLabel3: r.custom_label3,
          customLabel4: r.custom_label4
        }));
        setProducts(list);
        setHasMoreServerPages(!debouncedSearch && rows.length === PAGE_SIZE);
      } catch (e) {
        setErrorMsg(e?.message || 'Search error');
      } finally {
        setIsLoadingMore(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, merchantId]);

  const visibleProducts = useMemo(() => {
    return products;
  }, [products]);

  useEffect(() => {
    setPageIndex(1);
  }, [products]);

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(visibleProducts.length / PAGE_SIZE));
  }, [visibleProducts.length]);

  const pagedProducts = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return visibleProducts.slice(start, end);
  }, [visibleProducts, pageIndex]);

  // There can be a "next" page if the current page isn't full OR if the server
  // still has additional pages that we haven't fetched yet.
  const canNext = (pageIndex * PAGE_SIZE) < visibleProducts.length || hasMoreServerPages;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <img src="/merchantcenterlogo.svg" alt="Merchant Center" className="h-10" />
        </div>

        {/* Inventory summaries */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* By Brand */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm font-medium text-gray-800 mb-3">By Brand</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left">brand</th>
                    <th className="px-3 py-2 text-right">total</th>
                    <th className="px-3 py-2 text-right">in stock</th>
                    <th className="px-3 py-2 text-right">out of stock</th>
                    <th className="px-3 py-2 text-right">stock avail.</th>
                  </tr>
                </thead>
                <tbody>
                  {brandAgg.length ? brandAgg.map((row, i) => (
                    <tr key={row.key || i} className="border-t">
                      <td className="px-3 py-2">{row.key}</td>
                      <td className="px-3 py-2 text-right">{row.total.toLocaleString('tr-TR')}</td>
                      <td className="px-3 py-2 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {row.inStock.toLocaleString('tr-TR')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-50 text-gray-700 border border-gray-200">
                          {row.outOfStock.toLocaleString('tr-TR')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(() => {
                          const pct = row.total > 0 ? Math.round((row.inStock / row.total) * 100) : 0;
                          return (
                            <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 border border-blue-200">
                              {pct}%
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  )) : (
                    <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>{loading ? 'Loading…' : 'No data'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Category (hierarchical) */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="text-sm font-medium text-gray-800 mb-3">By Category</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-3 py-2 text-left">category</th>
                    <th className="px-3 py-2 text-right">total</th>
                    <th className="px-3 py-2 text-right">in stock</th>
                    <th className="px-3 py-2 text-right">out of stock</th>
                    <th className="px-3 py-2 text-right">stock avail.</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = [];
                    const sortChildren = (node) => Array.from(node.children.values()).sort((a, b) => b.total - a.total);
                    const walk = (node, level) => {
                      for (const child of sortChildren(node)) {
                        const hasChildren = child.children.size > 0;
                        rows.push(
                          <tr key={child.key} className="border-t">
                            <td className="px-3 py-2">
                              <div className="flex items-center">
                                <div style={{ marginLeft: level * 12 }} className="flex items-center gap-1">
                                  {hasChildren ? (
                                    <button
                                      onClick={() => toggleCat(child.key)}
                                      className="w-5 h-5 inline-flex items-center justify-center rounded border text-gray-600 hover:bg-gray-50"
                                      aria-label="toggle"
                                    >
                                      {expandedCats.has(child.key) ? '▾' : '▸'}
                                    </button>
                                  ) : (
                                    <span className="w-5 h-5 inline-flex items-center justify-center text-gray-300">•</span>
                                  )}
                                  <span title={child.key} className="max-w-[420px] truncate">{child.name}</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">{child.total.toLocaleString('tr-TR')}</td>
                            <td className="px-3 py-2 text-right">
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                                {child.inStock.toLocaleString('tr-TR')}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-50 text-gray-700 border border-gray-200">
                                {child.outOfStock.toLocaleString('tr-TR')}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {(() => {
                                const pct = child.total > 0 ? Math.round((child.inStock / child.total) * 100) : 0;
                                return (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-50 text-blue-700 border border-blue-200">
                                    {pct}%
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                        if (hasChildren && expandedCats.has(child.key)) {
                          walk(child, level + 1);
                        }
                      }
                    };
                    walk(categoryTree, 0);
                    if (!rows.length) {
                      return <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>{loading ? 'Loading…' : 'No data'}</td></tr>;
                    }
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card
            label="Active Products"
            value={activeRollup ? activeRollup.active.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Pending"
            value={activeRollup ? activeRollup.pending.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Disapproved"
            value={activeRollup ? activeRollup.disapproved.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Expiring"
            value={activeRollup ? activeRollup.expiring.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
        </div>

        {/* Products table */}
        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-800">Products</div>
            <div className="flex items-center gap-3">
              {/* Pagination */}
              <div className="flex items-center gap-1">
                <button
                  className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                  disabled={pageIndex <= 1}
                  onClick={() => setPageIndex(p => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <div className="text-xs text-gray-600 px-2">Page {pageIndex} / {pageCount}</div>
                <button
                  className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                  disabled={!canNext || isLoadingMore}
                  onClick={async () => {
                    const nextPage = pageIndex + 1;
                    const needCount = nextPage * PAGE_SIZE;
                    if (needCount > visibleProducts.length && hasMoreServerPages && !isLoadingMore) {
                      try {
                        setIsLoadingMore(true);
                        const offset = products.length;
                        const limit = PAGE_SIZE;
                        const pr = await fetch(
                          `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(
                            merchantId
                          )}&limit=${limit}&offset=${offset}`,
                          {
                            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
                            credentials: 'include'
                          }
                        );
                        const prText = await pr.text();
                        let prJson;
                        try {
                          prJson = JSON.parse(prText);
                        } catch {
                          prJson = { raw: prText };
                        }
                        if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
                        const rows = Array.isArray(prJson?.data) ? prJson.data : [];
                        const list = rows.map((r) => ({
                          offerId: r.offer_id,
                          productId: r.product_id,
                          title: r.title,
                          brand: r.brand,
                          availability: r.availability,
                          price: { value: r.price_value, currency: r.price_currency },
                          salePrice: r.sale_price_value
                            ? { value: r.sale_price_value, currency: r.sale_price_currency }
                            : null,
                          imageLink: r.image_link,
                          additionalImages: Array.isArray(r.additional_image_links)
                            ? r.additional_image_links
                            : [],
                          category: r.google_product_category,
                          link: r.link,
                          contentLanguage: r.content_language,
                          targetCountry: r.target_country,
                          condition: r.condition,
                          gtin: r.gtin,
                          customLabel0: r.custom_label0,
                          customLabel1: r.custom_label1,
                          customLabel2: r.custom_label2,
                          customLabel3: r.custom_label3,
                          customLabel4: r.custom_label4
                        }));
                        setProducts(prev => [...prev, ...list]);
                        if (rows.length < limit) {
                          setHasMoreServerPages(false);
                        }
                      } catch (e) {
                        console.error('Failed to load next batch of inventory products', e);
                        setHasMoreServerPages(false);
                      } finally {
                        setIsLoadingMore(false);
                      }
                    }
                    setPageIndex(p => p + 1);
                  }}
                >
                  {isLoadingMore ? 'Loading…' : 'Next'}
                </button>
              </div>
              {/* Search */}
              <div className="relative">
                <input
                  value={search}
                  onChange={(e)=>setSearch(e.target.value)}
                  placeholder="Search by title or ID…"
                  className="px-3 pr-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>
              <div className="text-xs text-gray-500">{visibleProducts.length} item(s)</div>
            </div>
          </div>
          {errorMsg ? <div className="text-sm text-red-600 mb-3">{errorMsg}</div> : null}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">image</th>
                  <th className="px-3 py-2 text-left">offer id</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-left">category</th>
                  <th className="px-3 py-2 text-left">brand</th>
                  <th className="px-3 py-2 text-left">availability</th>
                  <th className="px-3 py-2 text-left">price</th>
                </tr>
              </thead>
              <tbody>
                {pagedProducts.length ? pagedProducts.map((p, i) => {
                  const priceStr = p.price?.value ? `${Number(p.price.value).toLocaleString('tr-TR')} ${p.price.currency || ''}` : '';
                  const saleStr = p.salePrice?.value ? `${Number(p.salePrice.value).toLocaleString('tr-TR')} ${p.salePrice.currency || ''}` : '';
                  return (
                    <tr key={p.offerId || p.productId || i} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedProduct(p)}>
                      <td className="px-3 py-2">
                        {p.imageLink ? <img src={p.imageLink} alt={p.title} className="w-12 h-12 rounded object-cover border" /> : <div className="w-12 h-12 rounded bg-gray-100 border" />}
                      </td>
                      <td className="px-3 py-2">{p.offerId || '-'}</td>
                      <td className="px-3 py-2 max-w-[360px] truncate" title={p.title}>{p.title}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={p.category}>{p.category || '-'}</td>
                      <td className="px-3 py-2">{p.brand}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${p.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                          {p.availability || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {saleStr ? (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 line-through">{priceStr}</span>
                            <span className="text-rose-600 font-medium">{saleStr}</span>
                          </div>
                        ) : (
                          <span className="text-gray-800">{priceStr}</span>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>{loading ? 'Loading…' : 'No data'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Bottom pagination (duplicate controls for convenience) */}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
              disabled={pageIndex <= 1}
              onClick={() => setPageIndex(p => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div className="text-xs text-gray-600 px-2">Page {pageIndex} / {pageCount}</div>
            <button
              className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
              disabled={!canNext || isLoadingMore}
              onClick={async () => {
                const nextPage = pageIndex + 1;
                const needCount = nextPage * PAGE_SIZE;
                if (needCount > visibleProducts.length && hasMoreServerPages && !isLoadingMore) {
                  try {
                    setIsLoadingMore(true);
                    const offset = products.length;
                    const limit = PAGE_SIZE;
                    const pr = await fetch(
                      `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(
                        merchantId
                      )}&limit=${limit}&offset=${offset}`,
                      {
                        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
                        credentials: 'include'
                      }
                    );
                    const prText = await pr.text();
                    let prJson;
                    try {
                      prJson = JSON.parse(prText);
                    } catch {
                      prJson = { raw: prText };
                    }
                    if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
                    const rows = Array.isArray(prJson?.data) ? prJson.data : [];
                    const list = rows.map((r) => ({
                      offerId: r.offer_id,
                      productId: r.product_id,
                      title: r.title,
                      brand: r.brand,
                      availability: r.availability,
                      price: { value: r.price_value, currency: r.price_currency },
                      salePrice: r.sale_price_value
                        ? { value: r.sale_price_value, currency: r.sale_price_currency }
                        : null,
                      imageLink: r.image_link,
                      additionalImages: Array.isArray(r.additional_image_links)
                        ? r.additional_image_links
                        : [],
                      category: r.google_product_category,
                      link: r.link,
                      contentLanguage: r.content_language,
                      targetCountry: r.target_country,
                      condition: r.condition,
                      gtin: r.gtin,
                      customLabel0: r.custom_label0,
                      customLabel1: r.custom_label1,
                      customLabel2: r.custom_label2,
                      customLabel3: r.custom_label3,
                      customLabel4: r.custom_label4
                    }));
                    setProducts(prev => [...prev, ...list]);
                    if (rows.length < limit) {
                      setHasMoreServerPages(false);
                    }
                  } catch (e) {
                    console.error('Failed to load next batch of inventory products (bottom controls)', e);
                    setHasMoreServerPages(false);
                  } finally {
                    setIsLoadingMore(false);
                  }
                }
                setPageIndex(p => p + 1);
              }}
            >
              {isLoadingMore ? 'Loading…' : 'Next'}
            </button>
          </div>
        </div>
        {/* Product modal */}
        {selectedProduct && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setSelectedProduct(null)}>
            <div className="bg-white rounded-2xl shadow-2xl border w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-5 border-b md:border-b-0 md:border-r">
                  {(() => {
                    const imgs = [selectedProduct.imageLink, ...(selectedProduct.additionalImages || [])].filter(Boolean);
                    const main = imgs[activeImageIdx] || imgs[0];
                    return (
                      <>
                        <div className="aspect-square w-full rounded-xl overflow-hidden border bg-gray-50">
                          {main ? (
                            <img src={main} alt={selectedProduct.title} className="w-full h-full object-contain" />
                          ) : null}
                        </div>
                        {imgs.length > 1 && (
                          <div className="mt-3 grid grid-cols-5 gap-2">
                            {imgs.slice(0, 5).map((u, idx) => (
                              <img
                                key={idx}
                                src={u}
                                alt=""
                                onClick={() => setActiveImageIdx(idx)}
                                className={`h-14 w-full object-cover rounded border cursor-pointer ${idx === activeImageIdx ? 'ring-2 ring-blue-500' : ''}`}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="md:w-1/2 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedProduct.title}</h3>
                      <div className="text-sm text-gray-500">{selectedProduct.brand || '-'}</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setSelectedProduct(null)}>✕</button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    {selectedProduct.salePrice?.value ? (
                      <>
                        <span className="text-gray-400 line-through text-sm">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                        <span className="text-rose-600 text-xl font-semibold">{Number(selectedProduct.salePrice.value).toLocaleString('tr-TR')} {selectedProduct.salePrice.currency || ''}</span>
                      </>
                    ) : (
                      <span className="text-xl font-semibold text-gray-900">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                    )}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${selectedProduct.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                      {selectedProduct.availability || '-'}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                    <div className="text-gray-500">Offer ID</div><div className="text-gray-800">{selectedProduct.offerId || '-'}</div>
                    <div className="text-gray-500">Product ID</div><div className="text-gray-800">{selectedProduct.productId || '-'}</div>
                    <div className="text-gray-500">GTIN</div><div className="text-gray-800">{selectedProduct.gtin || '-'}</div>
                    <div className="text-gray-500">Condition</div><div className="text-gray-800">{selectedProduct.condition || '-'}</div>
                    <div className="text-gray-500">Language</div><div className="text-gray-800">{selectedProduct.contentLanguage || '-'}</div>
                    <div className="text-gray-500">Country</div><div className="text-gray-800">{selectedProduct.targetCountry || '-'}</div>
                    <div className="col-span-2 h-px bg-gray-100 my-1"></div>
                    <div className="text-gray-500">Custom label 0</div><div className="text-gray-800">{selectedProduct.customLabel0 || '-'}</div>
                    <div className="text-gray-500">Custom label 1</div><div className="text-gray-800">{selectedProduct.customLabel1 || '-'}</div>
                    <div className="text-gray-500">Custom label 2</div><div className="text-gray-800">{selectedProduct.customLabel2 || '-'}</div>
                    <div className="text-gray-500">Custom label 3</div><div className="text-gray-800">{selectedProduct.customLabel3 || '-'}</div>
                    <div className="text-gray-500">Custom label 4</div><div className="text-gray-800">{selectedProduct.customLabel4 || '-'}</div>
                  </div>
                  <div className="mt-5 flex items-center gap-2">
                    {selectedProduct.link ? (
                      <a href={selectedProduct.link} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800">View on site</a>
                    ) : null}
                    <button className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border hover:bg-gray-50" onClick={() => setSelectedProduct(null)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}




